import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Slider,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Alert,
  Tag,
  Space,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  TrophyOutlined,
  StopOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { Rate } from 'antd';
import templateService from 'services/templateService';

const { RangePicker } = DatePicker;

/**
 * ABTestManager - Create and manage A/B tests for templates
 */
const ABTestManager = ({ visible, onClose, templates }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tests, setTests] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [resultsModalVisible, setResultsModalVisible] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testResults, setTestResults] = useState(null);

  useEffect(() => {
    if (visible) {
      loadTests();
    }
  }, [visible]);

  const loadTests = async () => {
    setLoading(true);
    try {
      const response = await templateService.getABTests();
      if (response.success) {
        setTests(response.data || []);
      }
    } catch (error) {
      message.error('Failed to load A/B tests: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTest = async () => {
    try {
      const values = await form.validateFields();

      const testData = {
        testName: values.testName,
        templateAId: values.templateAId,
        templateBId: values.templateBId,
        analysisLevel: values.analysisLevel,
        startDate: values.dateRange[0].toISOString(),
        endDate: values.dateRange[1].toISOString(),
        trafficSplit: values.trafficSplit / 100, // Convert to decimal
      };

      setLoading(true);
      await templateService.createABTest(testData);
      message.success('A/B test created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      loadTests();
    } catch (error) {
      message.error('Failed to create A/B test: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewResults = async (test) => {
    try {
      setLoading(true);
      const response = await templateService.getABTestResults(test.testId);
      if (response.success) {
        setTestResults(response.data);
        setSelectedTest(test);
        setResultsModalVisible(true);
      }
    } catch (error) {
      message.error('Failed to load test results: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEndTest = async (testId) => {
    try {
      setLoading(true);
      await templateService.endABTest(testId);
      message.success('A/B test ended successfully');
      loadTests();
    } catch (error) {
      message.error('Failed to end test: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = [
    {
      title: 'Test Name',
      dataIndex: 'testName',
      key: 'testName',
    },
    {
      title: 'Template A',
      dataIndex: 'templateAName',
      key: 'templateAName',
    },
    {
      title: 'Template B',
      dataIndex: 'templateBName',
      key: 'templateBName',
    },
    {
      title: 'Traffic Split',
      dataIndex: 'trafficSplit',
      key: 'trafficSplit',
      render: (split) => `${Math.round(split * 100)}% / ${Math.round((1 - split) * 100)}%`,
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => `${formatDate(record.startDate)} - ${formatDate(record.endDate)}`,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Ended'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<BarChartOutlined />}
            onClick={() => handleViewResults(record)}
          >
            Results
          </Button>

          {record.isActive && (
            <Popconfirm
              title="End this A/B test?"
              description="This will stop the test and no longer show variant B to users."
              onConfirm={() => handleEndTest(record.testId)}
              okText="End Test"
              cancelText="Cancel"
            >
              <Button size="small" danger icon={<StopOutlined />}>
                End Test
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <StyledModal
        title="A/B Test Manager"
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
      >
        <Container>
          <ActionBar>
            <Alert
              message="A/B Testing"
              description="Compare two templates to determine which performs better based on user ratings and success rates."
              type="info"
              showIcon
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
              style={{ marginTop: 16 }}
            >
              Create A/B Test
            </Button>
          </ActionBar>

          <Table
            columns={columns}
            dataSource={tests}
            rowKey="testId"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        </Container>
      </StyledModal>

      {/* Create Test Modal */}
      <Modal
        title="Create A/B Test"
        open={createModalVisible}
        onOk={handleCreateTest}
        onCancel={() => setCreateModalVisible(false)}
        confirmLoading={loading}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Test Name"
            name="testName"
            rules={[{ required: true, message: 'Please enter test name' }]}
          >
            <Input placeholder="e.g., Sales Report Template Comparison" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Template A (Control)"
                name="templateAId"
                rules={[{ required: true, message: 'Please select template A' }]}
              >
                <Select placeholder="Select template A">
                  {templates.map((t) => (
                    <Select.Option key={t.templateId} value={t.templateId}>
                      {t.templateName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Template B (Variant)"
                name="templateBId"
                rules={[{ required: true, message: 'Please select template B' }]}
              >
                <Select placeholder="Select template B">
                  {templates.map((t) => (
                    <Select.Option key={t.templateId} value={t.templateId}>
                      {t.templateName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Analysis Level"
            name="analysisLevel"
            rules={[{ required: true, message: 'Please select analysis level' }]}
          >
            <Select placeholder="Select analysis level">
              <Select.Option value="page">Page Level</Select.Option>
              <Select.Option value="module">Module Level</Select.Option>
              <Select.Option value="dashboard">Dashboard Level</Select.Option>
              <Select.Option value="executive">Executive Level</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Traffic Split (% to Template A)"
            name="trafficSplit"
            initialValue={50}
            rules={[{ required: true }]}
          >
            <Slider
              min={0}
              max={100}
              marks={{
                0: '0% (All B)',
                25: '25%',
                50: '50/50',
                75: '75%',
                100: '100% (All A)',
              }}
            />
          </Form.Item>

          <Form.Item
            label="Test Period"
            name="dateRange"
            rules={[{ required: true, message: 'Please select test period' }]}
          >
            <RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Results Modal */}
      <Modal
        title={`A/B Test Results: ${selectedTest?.testName}`}
        open={resultsModalVisible}
        onCancel={() => setResultsModalVisible(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setResultsModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        {testResults && (
          <ResultsContainer>
            {/* Winner Banner */}
            {testResults.winner && (
              <Alert
                message={
                  <Space>
                    <TrophyOutlined style={{ fontSize: 20, color: '#faad14' }} />
                    <span>
                      <strong>Template {testResults.winner}</strong> is performing better by{' '}
                      <strong>{testResults.winnerMargin}%</strong>
                    </span>
                  </Space>
                }
                type="success"
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            <Row gutter={16}>
              {/* Template A Results */}
              <Col span={12}>
                <Card title="Template A (Control)" bordered>
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Statistic
                      title="Generations"
                      value={testResults.variantA.generations}
                      suffix="times"
                    />

                    <div>
                      <div style={{ marginBottom: 8 }}>
                        <Text>Success Rate</Text>
                      </div>
                      <Progress
                        percent={testResults.variantA.successRate}
                        status="active"
                        strokeColor="#52c41a"
                      />
                    </div>

                    <div>
                      <div style={{ marginBottom: 8 }}>
                        <Text>Average Rating</Text>
                      </div>
                      <Rate disabled value={testResults.variantA.avgRating} allowHalf />
                      <Text style={{ marginLeft: 8 }}>
                        ({testResults.variantA.avgRating?.toFixed(1)})
                      </Text>
                    </div>

                    <Statistic
                      title="Avg Generation Time"
                      value={
                        testResults.variantA.avgDuration
                          ? (testResults.variantA.avgDuration / 1000).toFixed(2)
                          : 0
                      }
                      suffix="s"
                    />
                  </Space>
                </Card>
              </Col>

              {/* Template B Results */}
              <Col span={12}>
                <Card title="Template B (Variant)" bordered>
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Statistic
                      title="Generations"
                      value={testResults.variantB.generations}
                      suffix="times"
                    />

                    <div>
                      <div style={{ marginBottom: 8 }}>
                        <Text>Success Rate</Text>
                      </div>
                      <Progress
                        percent={testResults.variantB.successRate}
                        status="active"
                        strokeColor="#1890ff"
                      />
                    </div>

                    <div>
                      <div style={{ marginBottom: 8 }}>
                        <Text>Average Rating</Text>
                      </div>
                      <Rate disabled value={testResults.variantB.avgRating} allowHalf />
                      <Text style={{ marginLeft: 8 }}>
                        ({testResults.variantB.avgRating?.toFixed(1)})
                      </Text>
                    </div>

                    <Statistic
                      title="Avg Generation Time"
                      value={
                        testResults.variantB.avgDuration
                          ? (testResults.variantB.avgDuration / 1000).toFixed(2)
                          : 0
                      }
                      suffix="s"
                    />
                  </Space>
                </Card>
              </Col>
            </Row>

            {/* Statistical Significance */}
            <Card title="Statistical Analysis" style={{ marginTop: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Sample Size"
                    value={testResults.totalSamples || 0}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Confidence Level"
                    value={testResults.confidenceLevel || 0}
                    suffix="%"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Statistical Significance"
                    value={testResults.isSignificant ? 'Yes' : 'No'}
                    valueStyle={{
                      color: testResults.isSignificant ? '#52c41a' : '#ff4d4f',
                    }}
                  />
                </Col>
              </Row>
            </Card>
          </ResultsContainer>
        )}
      </Modal>
    </>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 24px;
  }
`;

const Container = styled.div``;

const ActionBar = styled.div`
  margin-bottom: 24px;
`;

const ResultsContainer = styled.div``;

const Text = styled.span`
  font-size: 14px;
  color: #595959;
`;

export default ABTestManager;
